import React, { useState, useCallback, useEffect } from "react";
import * as Icon from "react-bootstrap-icons";
import { toast } from "react-toastify";
import axios from "../../utils/axiosInstance";
import WordEditor from "./WordEditor";
import Spreadsheet from "../Spreadsheet";

const API = process.env.REACT_APP_API_URL || "";

export default function GukoraAmaraporo() {
  const [activeView, setActiveView] = useState("list"); // list, word, excel
  const [activeReportId, setActiveReportId] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("updated");
  const [filterType, setFilterType] = useState("all");
  const [showFavorites, setShowFavorites] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterType !== "all") params.type = filterType;
      if (showFavorites) params.favorite = "true";
      params.sort = sortBy;
      const res = await axios.get(`${API}/api/amaraporo`, { params });
      setReports(res.data);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterType, showFavorites, sortBy]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const createReport = useCallback(async (type) => {
    try {
      const res = await axios.post(`${API}/api/amaraporo`, {
        title: type === "word" ? "Raporo" : "Spreadsheet",
        report_type: type,
        content: type === "word" ? "<p>Start writing...</p>" : "",
      });
      setActiveReportId(res.data.id);
      setActiveView(type);
      toast.success("Raporo yashizweho");
    } catch {
      toast.error("Failed to create report");
    }
  }, []);

  const openReport = useCallback((id, type) => {
    setActiveReportId(id);
    setActiveView(type);
  }, []);

  const saveReport = useCallback(async (data) => {
    try {
      if (activeReportId) {
        await axios.put(`${API}/api/amaraporo/${activeReportId}`, data);
        toast.success("Saved");
        fetchReports();
      } else {
        const res = await axios.post(`${API}/api/amaraporo`, data);
        setActiveReportId(res.data.id);
        toast.success("Created & saved");
        fetchReports();
      }
    } catch {
      toast.error("Save failed");
    }
  }, [activeReportId, fetchReports]);

  const deleteReport = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/api/amaraporo/${id}`);
      toast.success("Deleted");
      if (activeReportId === id) { setActiveReportId(null); setActiveView("list"); }
      fetchReports();
    } catch {
      toast.error("Delete failed");
    }
  }, [activeReportId, fetchReports]);

  const toggleFavorite = useCallback(async (id) => {
    try {
      const res = await axios.patch(`${API}/api/amaraporo/${id}/favorite`);
      toast.info(res.data.is_favorite ? "Added to favorites" : "Removed from favorites");
      fetchReports();
    } catch {
      toast.error("Failed");
    }
  }, [fetchReports]);

  const duplicateReport = useCallback(async (id) => {
    try {
      await axios.post(`${API}/api/amaraporo/${id}/duplicate`);
      toast.success("Duplicated");
      fetchReports();
    } catch {
      toast.error("Duplicate failed");
    }
  }, [fetchReports]);

  const handleBack = useCallback(() => {
    setActiveReportId(null);
    setActiveView("list");
    fetchReports();
  }, [fetchReports]);

  const filtered = reports;

  if (activeView === "word") {
    return <WordEditor reportId={activeReportId} onSave={saveReport} onBack={handleBack} />;
  }

  if (activeView === "excel") {
    return <Spreadsheet reportId={activeReportId} onSave={saveReport} onBack={handleBack} />;
  }

  return (
    <div style={{ padding: "16px 20px", height: "100%", overflow: "auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
            <Icon.Files size={24} color="#2563eb" /> Gukora Amaraporo
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8" }}>Create and manage professional reports, documents, and spreadsheets</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => createReport("word")}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 600, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
            <Icon.FileWord size={18} /> New Word Document
          </button>
          <button onClick={() => createReport("excel")}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", background: "#217346", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 600, boxShadow: "0 2px 8px rgba(33,115,70,0.3)" }}>
            <Icon.FileExcel size={18} /> New Excel Spreadsheet
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <Icon.Search size={15} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search reports..."
            style={{ width: "100%", padding: "8px 10px 8px 32px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none" }} />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={filterSelectStyle}>
          <option value="all">All Types</option>
          <option value="word">Word</option>
          <option value="excel">Excel</option>
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={filterSelectStyle}>
          <option value="updated">Last Updated</option>
          <option value="title">Title A-Z</option>
          <option value="title_desc">Title Z-A</option>
          <option value="created">Newest First</option>
          <option value="created_asc">Oldest First</option>
        </select>
        <button onClick={() => setShowFavorites(p => !p)}
          style={{ padding: "8px 14px", border: `1px solid ${showFavorites ? "#f59e0b" : "#e2e8f0"}`, borderRadius: 8, background: showFavorites ? "#fffbeb" : "#fff", cursor: "pointer", fontSize: 13, color: showFavorites ? "#d97706" : "#333", display: "flex", alignItems: "center", gap: 6 }}>
          <Icon.Star size={15} fill={showFavorites ? "#f59e0b" : "none"} /> {showFavorites ? "Favorites" : "All"}
        </button>
      </div>

      {/* Report List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
          <div style={{ width: 40, height: 40, border: "3px solid #e2e8f0", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          Loading reports...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 80, color: "#94a3b8" }}>
          <Icon.Files size={60} style={{ opacity: 0.3, marginBottom: 16 }} />
          <h3 style={{ color: "#64748b", fontWeight: 600, marginBottom: 8 }}>No Reports Yet</h3>
          <p style={{ fontSize: 14, marginBottom: 20 }}>Create your first report to get started</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button onClick={() => createReport("word")}
              style={{ padding: "10px 20px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
              <Icon.FileWord size={16} style={{ marginRight: 6 }} /> New Word Document
            </button>
            <button onClick={() => createReport("excel")}
              style={{ padding: "10px 20px", background: "#217346", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
              <Icon.FileExcel size={16} style={{ marginRight: 6 }} /> New Excel Spreadsheet
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
          {filtered.map(r => (
            <div key={r.id} onClick={() => openReport(r.id, r.report_type)}
              style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, cursor: "pointer", transition: "all 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "#2563eb"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "#e2e8f0"}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: r.report_type === "word" ? "rgba(37,99,235,0.1)" : "rgba(33,115,70,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {r.report_type === "word" ? <Icon.FileWord size={22} color="#2563eb" /> : <Icon.FileExcel size={22} color="#217346" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", display: "flex", gap: 8 }}>
                  <span>{r.report_type === "word" ? "Word Document" : "Excel Spreadsheet"}</span>
                  <span>·</span>
                  <span>{new Date(r.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
              <button onClick={e => { e.stopPropagation(); toggleFavorite(r.id); }}
                style={{ border: "none", background: "none", cursor: "pointer", padding: 4, color: r.is_favorite ? "#f59e0b" : "#cbd5e1" }}>
                <Icon.Star size={16} fill={r.is_favorite ? "#f59e0b" : "none"} />
              </button>
              <div style={{ position: "relative" }}>
                <button onClick={e => { e.stopPropagation(); const m = e.currentTarget.nextElementSibling; m.style.display = m.style.display === "block" ? "none" : "block"; }}
                  style={{ border: "none", background: "none", cursor: "pointer", padding: 4, color: "#94a3b8" }}>
                  <Icon.ThreeDotsVertical size={16} />
                </button>
                <div style={{ display: "none", position: "absolute", right: 0, top: "100%", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 10, minWidth: 140 }}>
                  {[
                    { icon: Icon.Pencil, label: "Rename", onClick: async () => { const t = prompt("Title:", r.title); if (t) { await axios.put(`${API}/api/amaraporo/${r.id}`, { title: t }); fetchReports(); } } },
                    { icon: Icon.Copy, label: "Duplicate", onClick: () => duplicateReport(r.id) },
                    { icon: Icon.Trash, label: "Delete", onClick: () => { if (window.confirm("Delete this report?")) deleteReport(r.id); } },
                  ].map(({ icon: Ico, label, onClick: handler }) => (
                    <div key={label} onClick={e => { e.stopPropagation(); handler(); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", cursor: "pointer", fontSize: 13, color: label === "Delete" ? "#ef4444" : "#333", borderRadius: 6 }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <Ico size={14} /> {label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const filterSelectStyle = {
  padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8,
  fontSize: 13, outline: "none", background: "#fff", cursor: "pointer",
};
